import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
// services
import { MasterService } from "../../../services/master.service";
import { MessageService } from "../../../services/message.service";
import { StoreService } from "../../../services/store.service";
import { SecurityService } from "src/app/services/security.service";
import * as languageLibrary from '../../../services/language';
import { SCROLL_BOTTOM_OUT } from "src/app/constants/list";
import { AuditEntry } from "src/app/models/audit.model";
import { User } from "src/app/models/user.model";
import { Subscription } from "rxjs";

@Component({
    selector: "audit-report",
    templateUrl: "./audit-report.component.html",
    styleUrls: ["./audit-report.component.scss"],
    standalone: false
})
export class AuditReportComponent implements OnInit, OnDestroy {

    public FILTER_SPAN_KEY = 'flt-usraud-when'

    public FILTER_USER_KEY = 'flt-useraud-user'

    public FILTER_MODULE_KEY = 'flt-useraud-module'

    public FILTER_ACTION_KEY = 'flt-useraud-action'

    /** define if is loading */ 
    public loading: boolean = false

    // save the language
    public language: string | null = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    public words = languageLibrary.language

    // A list od user audit log entries for the report.
    protected entries: AuditEntry[] = []

    // A list od user audit log entries for the report.
    protected users: User[] = []

     // Current page the user is viewing (last loaded).
    public page = 1

    // The number contacts currently being displayed.
    public displayed = 0

    // Indicates whether or not there are no more records to load.
    public exhausted = false

    // Total number of records available
    public records: number = 0

    // The search form group
    public form: FormGroup | null = null

    // Keep track of how the user wants to sort list.
    private sortables: Object = {'createdAt': 'DESC'}

    // The audit entry whose meta data is currently being viewed.
    protected viewable: AuditEntry | null = null

    private subscriptions: Subscription[] = []

    get metaIsArray(): boolean {
        if (!this.viewable || (!this.viewable.meta)) {
            return false
        }
        
        return Array.isArray(this.viewable.meta)
    }

    constructor(private master: MasterService, private ms: MessageService, protected store: StoreService, private router: Router, private fb: FormBuilder, protected security: SecurityService) {
        this.form = this.fb.group({
            span: new FormControl<string>('', []),
            from: new FormControl<string>('', []),
            to: new FormControl<string>('', []),
            user: new FormControl<number>(0, []),
            module: new FormControl<string>('', []),
            action: new FormControl<string>('', [])
        })
    }

    ngOnInit() {
        this.subscriptions.push(
            this.form.get('span').valueChanges.subscribe(span => {
                this.writeFilters()
                this.reset()            
                this.fetch()
            })
        )

        this.subscriptions.push(
            this.form.get('user').valueChanges.subscribe(user => {
                this.writeFilters()
                this.reset()            
                this.fetch()
            })
        )

        this.subscriptions.push(
            this.form.get('module').valueChanges.subscribe(module => {
                this.writeFilters()
                this.reset()            
                this.fetch()
            })
        )

        this.readFilters()
        this.fetchUsers()
        this.fetch()
    }

    ngOnDestroy(): void {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }

        this.subscriptions = []
    }

    /**
     * Reset the pagination parameters and scroll to the top
    */
    private reset() {
        this.page = 1
        this.exhausted = false
        this.displayed = 0

        const list = document.querySelector('#listing')
        if (!list) {
            return
        }

        list.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        })
    }

    /**
     * Handle scroll events of the list container and load 
     * the next page when avilable and the user scrolled 
     * to the bottom.
     * @param e The scroll event that trigggered this handler.
     */
    public scrolled = (e): void => {
        if (!e || !e.target) {
            return
        }

        if (this.exhausted) {
            return
        }

        if (this.loading) {
            return
        }

        const location = Math.abs(e.target.scrollHeight - e.target.clientHeight - e.target.scrollTop)

        if (location >= SCROLL_BOTTOM_OUT) {
            return
        }

        this.page = this.page + 1
        this.fetch()
    }

    /**
     * 
     * @param field The field that user wants to sort by. 
     */
    public sort = (field: string): void => {
        if (!field || (field.length <= 0)) {
            return
        }

        if (this.sortables[field]) { // need to toggle the direction?
            switch (this.sortables[field]) {
                case 'ASC':
                    this.sortables[field] = 'DESC'
                    break
                default:
                    this.sortables[field] = 'ASC'
                    break
            }

            this.reset()
            this.fetch()
            return
        }

        this.sortables = {}
        this.sortables[field] = 'ASC'
        this.reset()
        this.fetch()
    }

    public sortedAsc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'ASC')
    }

    public sortedDesc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'DESC')
    }

    /**
     * Query for a list of sales.
     * @param lot Unique identification of the selected lot
     */
    public fetch = async () => {
        if (this.security.notPermitted('reports', 'user audit log', 'view')) {
            return
        }

        if (this.loading) { // already getting next page
            return
        }
  
        if (this.exhausted) { // currently showing all available records?
            return
        }
  
        this.loading = true

        let spanParam = ''
        const spanKey = this.form.get('span').value
        if (spanKey && (spanKey.length > 0)) {
            switch (spanKey) {
                case 'custom':
                    const from = this.form.get('from').value
                    const to = this.form.get('to').value
                    spanParam = `&from=${from}&to=${to}`
                    break
                default:
                    spanParam = `&span=${spanKey}`
                    break
            }
        }

        let userParam = ''
        const user = parseInt(this.form.get('user').value)
        if (user && (!isNaN(user)) && (user > 0)) {
            userParam = `&userId=${user}`
        }

        let moduleParam = ''
        const module = this.form.get('module').value
        if (module && (module.trim().length > 0)) {
            moduleParam = `&module=${encodeURIComponent(module)}`
        }
  
        let sortParam = ''
        if (this.sortables && (Object.keys(this.sortables).length > 0)) {
            const sorts = []
            for (const key of Object.keys(this.sortables)) {
                let direction = ''
                if (this.sortables[key] && this.sortables[key].length > 0) {
                    direction = `|${this.sortables[key]}`
                }
  
                sorts.push(`${key}${direction}`)
            }
  
            sortParam = `&sort=${sorts.join(',')}`
        }

        this.loading = true
        await this.master.getAsync(`reports/audit?page=${this.page}${sortParam}${spanParam}${userParam}${moduleParam}`).then(res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }

            this.records = res.data.records
            this.displayed = Math.min(this.records, (res.data.page * res.data.ipp))
            this.exhausted = (res.data.next === null)

            if (this.page > 1) {
                this.entries = this.entries.concat(res.data.entries)
                return
            }

            this.entries = res.data.entries
        })
    }

    private fetchUsers() {
        this.master.get(`users`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.users = res.data.users
        })
    }

    /**
     * Check session storage for stored filters and apply them if found.
     */
    private readFilters = () => {
        if (!sessionStorage) {
            return
        }

        const spanFilter = sessionStorage.getItem(this.FILTER_SPAN_KEY)
        if (spanFilter && (spanFilter.length > 0)) {
            if (spanFilter.startsWith('custom')) {
                const items = spanFilter.split(';')
                if (!items || (items.length !== 3)) {
                    return
                }

                this.form.get('span').setValue(items[0], {emitEvent: false})
                this.form.get('from').setValue(items[1], {emitEvent: false})
                this.form.get('to').setValue(items[2], {emitEvent: false})    

                return
            }

            this.form.get('span').setValue(spanFilter, {emitEvent: false})
        }
        else {
            this.form.get('span').setValue('today', {emitEvent: false})
            sessionStorage.setItem(this.FILTER_SPAN_KEY, `today`)
        }

        const userFilter = sessionStorage.getItem(this.FILTER_USER_KEY)
        if (userFilter && (userFilter.length > 0)) {
            this.form.get('user').setValue(userFilter.toString(), {emitEvent: false})
        }

        const moduleFilter = sessionStorage.getItem(this.FILTER_MODULE_KEY)
        if (moduleFilter && (moduleFilter.length > 0)) {
            this.form.get('module').setValue(moduleFilter.toString(), {emitEvent: false})
        }
    }

    /**
     * Save the existing list fitlers to session storage for later retrieval.
     */
    private writeFilters = () => {
        if (!sessionStorage) {
            return
        }

        const spanFilter = this.form.get('span').value
        if (!spanFilter || (spanFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_SPAN_KEY)
        }
        else {
            let fromTo = ''
            if (spanFilter === 'custom') {
                const from = this.form.get('from').value
                const to = this.form.get('to').value
                fromTo = `;${from};${to}`
            }

            sessionStorage.setItem(this.FILTER_SPAN_KEY, `${spanFilter}${fromTo}`)
        }

        const userFilter = this.form.get('user').value
        if (!userFilter || (userFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_USER_KEY)
        }
        else {
            sessionStorage.setItem(this.FILTER_USER_KEY, userFilter)
        }

        const moduleFilter = this.form.get('module').value
        if (!moduleFilter || (moduleFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_MODULE_KEY)
        }
        else {
            sessionStorage.setItem(this.FILTER_MODULE_KEY, moduleFilter)
        }
    }

    /**
     * Handle date span choices by the user.
     * @param choice 
     */
    public spanned = (choice: Object) => {
        if (!choice || (choice['key'] === undefined) || (choice['key'] === null)) {
            return
        }

        if (choice['key'] === 'custom') {
            const from = choice['from']
            const to = choice['to']
            if (!from || !to) {
                return
            }

            this.form.get('from').setValue(from, {emitEvent: false})
            this.form.get('to').setValue(to, {emitEvent: false})    
        }

        this.form.get('span').setValue(choice['key'])
    }

    /**
     * 
     * @param entry 
     */
    protected view = (entry: AuditEntry) => {
        if (!entry || !entry.meta) {
            this.viewable = null
            return
        }

        const btn = document.getElementById('btn-toggle-audit-meta-view') as HTMLButtonElement
        if (!btn) {
            this.viewable = null
            return
        }

        this.viewable = entry
        btn.click()
    }

     /**
     * Return audit log details when available.
     */
     protected detail = () => {
        if (!this.viewable || !this.viewable.meta) {
            return ''
        }

        return JSON.stringify(this.viewable.meta, null, '\t')
    }

    /**
     * 
     * @param action 
     * @returns 
     */
    protected color = (action: string): string => {
        if (!action || (action.length <= 0)) {
            return 'muted'
        }

        const verbAndSource = action.split(':')
        if (verbAndSource.length <= 0) {
            return 'muted'
        }

        switch (verbAndSource[0].toLocaleLowerCase()) {
            case 'auth':
                if (action.startsWith('auth:failed')) {
                    return 'auth-failed'
                }

                return verbAndSource[0].toLocaleLowerCase()
            default:
                return verbAndSource[0].toLocaleLowerCase()
        }
    }
    
    /**
     * listen to output of navbar
    */ 
    protected listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'setPermissions':
                this.reset()
                this.fetch()
                break
            case 'changeLot':
                break
        }
    }
}
